import { FC, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { UButton, UModalPopup, UText } from "@/ui-component/components"
import { Box, Checkbox, FormControlLabel } from "@/ui-component/mui.components"
import { RootState } from "@/core/app/store"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { useNavigate, useParams } from "react-router-dom"
import { updateIsBackstopTncAccepted } from "@/core/app/slices/order/orderSlice"
import { formatDate } from "@/modules/Patient/PatientList/util/commonUtil"
import { formatCurrency } from "@/core/utils/formatters"
import { openNewTab } from "@/modules/Patient/config/tab.config"
import { useTabRef } from "@/routes/TabProvider"

interface BackstopProps {
    showAlert: boolean
    setShowAlert: (showAlert: boolean) => void
    onContinue: () => void
}

const Backstop: FC<BackstopProps> = ({
    showAlert,
    setShowAlert,
    onContinue
}) => {
    const tabRefs = useTabRef()
    const { orgId } = useParams()

    const [isChecked, setIsChecked] = useState(false)
    const { t } = useTranslation("common")
    const navigate = useNavigate()
    const { patientId, caseId } = useParams()
    const dispatch = useAppDispatch()
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked)
        dispatch(updateIsBackstopTncAccepted(event.target.checked))
    }
    const { quotation } = useAppSelector(
        (state: RootState) => state.orderService,
    )
    const { user } = useAppSelector((state: RootState) => state.userService)

    return (
        <UModalPopup
            isAlert={showAlert}
            title={
                <UText
                    variant="h6"
                >
                    {t("backstop.title")}
                </UText>
            }
            sxModalProps={{
                ".MuiDialog-paper": {
                    maxWidth: 600,
                },
                "#titleCntr": {
                    padding: "16px 24px",
                },
                "#contentCntr": {
                    padding: "20px 24px",
                },
                "#btnListCntr": {
                    padding: 1,
                    gap: 1,
                    display: "flex",
                    justifyContent: "space-between",
                },
            }}
            content={
                <Box component="div">
                    <UText variant="body1" sxProp={{ fontWeight: 700, lineHeight: "150%" }}>
                        {t("backstop.firstOrder", {
                            date: formatDate(quotation?.backstop?.startDate),
                            used: formatCurrency(quotation?.backstop?.totalSpend),
                            total: formatCurrency(quotation?.backstop?.amount)
                        })}
                    </UText>
                    <UText variant="body1" sxProp={{ display: "block", mb: 4, mt: 4 }}>
                        <Trans components={{ bold: <b /> }}>
                            {t("backstop.selectingBundle")}
                        </Trans>
                    </UText>
                    <UText variant="body1" sxProp={{ display: "block", mb: 1, fontWeight: 700 }}>
                        {t("backstop.chooseOption")}
                    </UText>
                    <Box component="ul" sx={{ margin: 0, pl: 4, mb: 2 }}>
                        <Box component="li">
                            <UText variant="body1" sxProp={{ display: "inline" }}>
                                <strong>{t("backstop.cancelLabel")}</strong>
                                {t("backstop.cancelDescription")}
                            </UText>
                        </Box>
                        <Box component="li">
                            <UText variant="body1" sxProp={{ display: "inline" }}>
                                <strong>{t("backstop.reviewLabel")}</strong>
                                {t("backstop.reviewDescription")}
                            </UText>
                        </Box>
                        <Box component="li">
                            <UText variant="body1" sxProp={{ display: "inline" }}>
                                <strong>{t("backstop.continueLabel")}</strong>
                                {t("backstop.continueDescription")}
                            </UText>
                        </Box>
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                required 
                                checked={isChecked}
                                onChange={handleCheckboxChange}
                                sx={{ pt: 0 }} 
                            />
                        }
                        label={t("backstop.understand")}
                        sx={{
                            marginTop: 2,
                            alignItems: "flex-start",
                            pt: 0
                        }}
                    />
                </Box>
            }
            btnList={[
                <UButton
                    key="cancel"
                    variant="shade"
                    btnText={t("backstop.cancel")}
                    onClickHandler={() => setShowAlert(false)}
                    sxProp={{
                        height: 36,
                    }}
                />,
                <Box key="right-buttons" sx={{ display: "flex", gap: 1 }}>
                    <UButton
                        key="review"
                        variant="outlined"
                        btnText={t("backstop.reviewOrders")}
                        onClickHandler={() => {
                            if (patientId) {
                                openNewTab({
                                    route: `/overview/patient/${patientId}/case/${caseId}?from=orderBackstop`,
                                    tabRefs,
                                    setOpenTabExceededModal:()=>{},
                                    orgId,
                                    isAllowToViewAllOrg: user?.isAllowToViewAllOrg,
                                })
                            }
                        }}
                        sxProp={{
                            height: 36,
                        }}
                    />
                    <UButton
                        key="continue"
                        variant="contained"
                        btnText={t("backstop.continue")}
                        onClickHandler={onContinue}
                        disabled={!isChecked}
                        sxProp={{
                            height: 36,
                        }}
                    />
                </Box>
            ]}
        />
    )
}

export default Backstop